import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { CnResult } from '@cainiaofe/cn-ui';
import { CnChart } from '@/components/cn-chart';
import {
  calculateAdvancedConfig,
  calculateFeature,
  getChartCommonStyle,
  isDesignMode,
} from '@/lowcode/common/util/util';
import { CnBarChartProps } from '@/lowcode/common/comoponents/cn-bar-chart/type';
import { getBarChartOptions } from '@/lowcode/common/comoponents/cn-bar-chart/util';
import { getIntervalFields } from '@/lowcode/common/setter/interval-setter/util';
import useFilterSearchListener from '../common/hooks/useFilterSearchListener';
import { FeaturePosition } from '@/types/feature-position';
import useStopLoop from '@/lowcode/common/hooks/useStopLoop';
import { useData } from '@/lowcode/common/hooks/useData';
import { EmptyData } from '../common/empty-data';
import WrapperContainer from '../common/wrapper-container';

const CnBarChart = forwardRef((props: CnBarChartProps, ref) => {
  const { dataFrom, filterConfig, chartStyle, forceRequest, _context } = props || {};
  const { requestConfig } = dataFrom || {};
  const { xField, yField } = getIntervalFields(requestConfig);
  const realYField = yField?.[0]?.fieldName;
  const yAliasName = yField?.[0]?.aliasName || realYField;
  const { _bindFilter } = filterConfig || {};

  const {
    data: listData,
    error,
    loading,
    refreshData: sendRequest,
  } = useData({
    config: { ...props, isDesign: isDesignMode(props) },
    source: 'chart',
  });

  useFilterSearchListener({
    _bindFilter,
    sendRequest,
    _context,
  });

  useStopLoop({
    _nodeId: props?._nodeId,
  });

  useEffect(() => {
    if (forceRequest) {
      sendRequest();
    }
  }, [forceRequest]);

  useImperativeHandle(ref, () => ({
    load: sendRequest,
  }));

  if (!listData || !realYField || !xField) {
    return (
      <EmptyData
        height={chartStyle.height}
        emptyImg={
          'https://gw.alicdn.com/imgextra/i3/O1CN01HMS8vB1Slx6IqqL1t_!!6000000002288-2-tps-500-260.png'
        }
      />
    );
  }

  const renderMain = () => {
    if (!listData?.length) {
      return <CnResult type="empty" title="数据为空" subTitle="暂无数据" />;
    }

    const defaultOptions = {
      ...getBarChartOptions(chartStyle),
      dataset: {
        dimensions: [
          { name: realYField, displayName: yAliasName },
          ...(xField?.map((x) => {
            return { name: x.fieldName, displayName: x.aliasName };
          }) || []),
        ],
        source: listData,
      },
      series: xField?.map((x) => {
        return {
          name: x.aliasName,
          type: 'bar',
          barMaxWidth: 20,
          encode: {
            x: x.fieldName,
            y: realYField,
          },
        };
      }),
    };

    const finalOptions = calculateAdvancedConfig({
      props,
      options: calculateFeature({
        props,
        options: defaultOptions,
        position: FeaturePosition.column,
        sendRequest,
      }),
      state: _context?.state,
    });

    return <CnChart {...getChartCommonStyle(props)} {...finalOptions} />;
  };

  return (
    <WrapperContainer loading={loading} error={error}>
      {renderMain()}
    </WrapperContainer>
  );
});

CnBarChart.displayName = 'CnBarChartPro';
export default CnBarChart;

export { CnBarChart };
